import { Subject } from "rxjs"
import { MarketId, BoardId } from "../../model/common"
import { Deal, Funding, Order } from "../../model/trade"
import { TradeKey } from "../../model/api"
import { MarketError } from "../../error/codes"

let i = 0

export type LogEntryType = 'deal' | 'funding' | 'order' | 'ws.public.connect' | 'ws.public.disconnect' | 'ws.private.connect' | 'ws.private.disconnect' | 'error.market'
export type LogEntryTag = 'deal' | 'funding' | 'order' | 'network' | 'error' | string

export abstract class LogEntry {
  readonly id: number
  readonly type: LogEntryType
  readonly timestamp: number
  readonly tags: LogEntryTag[]
  constructor(params: {type: LogEntryType, timestamp?: number, tags?: LogEntryTag[]}) {
    this.type = params.type
    this.timestamp = params.timestamp ?? Date.now()
    this.tags = params.tags ?? []
    this.id = ++i
  }
  addTags(...rest: LogEntryTag[]) {
    this.tags.push(...rest)
    return this
  }
}

export abstract class ResolvableLogEntry extends LogEntry {
  private _isResolved = false
  private resolveSubject = new Subject<void>()
  readonly resolve$ = this.resolveSubject.asObservable()
  get isResolved() {
    return this._isResolved
  }
  resolve() {
    this._isResolved = true
    this.resolveSubject.next()
    this.resolveSubject.complete()
  }
}

export class WsPublicConnectEntry extends LogEntry {
  constructor(public readonly market: MarketId, public readonly board?: BoardId) {
    super({type: 'ws.public.connect', tags: ['network']})
  }
}

export class WsPublicDisconnectEntry extends ResolvableLogEntry {
  constructor(public readonly market: MarketId, public readonly board?: BoardId) {
    super({type: 'ws.public.disconnect', tags: ['network']})
  }
}

export class WsPrivateConnectEntry extends LogEntry {
  constructor(public readonly key: TradeKey, public readonly board?: BoardId) {
    super({type: 'ws.private.connect', tags: ['network']})
  }
}

export class WsPrivateDisconnectEntry extends ResolvableLogEntry {
  constructor(public readonly key: TradeKey, public readonly board?: BoardId) {
    super({type: 'ws.private.disconnect', tags: ['network']})
  }
}

export class OrderEntry extends LogEntry {
  constructor(public readonly order: Order) {
    super({type: 'order', timestamp: order.updateTimestamp, tags: ['order']})
  }
}

export class DealEntry extends LogEntry {
  constructor(public readonly deal: Deal) {
    super({type: 'deal', timestamp: deal.timestamp, tags: ['deal']})
  }
}

export class FundingEntry extends LogEntry {
  constructor(public readonly funding: Funding) {
    super({type: 'funding', timestamp: funding.timestamp, tags: ['funding']})
  }
}

export class MarketErrorEntry extends LogEntry {
  constructor(public readonly key: TradeKey, public readonly error: MarketError) {
    super({type: 'error.market', timestamp: Date.now(), tags: ['error']})
  }
}
